import React from "react";
import { Box, Typography, Grid } from "@mui/material";
import PetsIcon from "@mui/icons-material/Pets";
import type { Dog } from "../types";
import { QuizStyledCard } from "../components/shared/QuizStyledCard";
import { QuizButton } from "../components/shared/QuizButton";
import { DogSummaryCard } from "../components/DogPage/DogSummaryCard";
import { QUIZ } from "@/theme/quizTokens";

export function DogsPage({
    dogs,
    onViewDog,
    onEditDog,
    onDeleteDog,
    onRestoreDog,
}: {
    dogs: Dog[];
    onViewDog: (dogId: string) => void;
    onEditDog: (dogId: string) => void;
    onDeleteDog?: (dogId: string) => void;
    onRestoreDog?: (dogId: string) => void;
}) {
    const hasDogs = dogs && dogs.length > 0;

    const sortedDogs = React.useMemo(() => {
        return [...dogs].sort((a, b) => {
            if (a.deleted && !b.deleted) return 1;
            if (!a.deleted && b.deleted) return -1;
            return 0;
        });
    }, [dogs]);

    const activeDogCount = React.useMemo(() => {
        return dogs.filter(d => !d.deleted).length;
    }, [dogs]);

    return (
        <Box>
            <Box sx={{ mb: 3 }}>
                <Typography
                    sx={{
                        fontSize: { xs: 22, sm: 26, md: 28 },
                        fontWeight: 800,
                        color: QUIZ.color.brown,
                        mb: 1,
                    }}
                >
                    Your Dogs
                </Typography>
                <Typography
                    sx={{
                        fontFamily: QUIZ.font.body,
                        fontSize: { xs: "14px", sm: "15px", md: "16px" },
                        lineHeight: { xs: "20px", md: "24px" },
                        color: QUIZ.color.brown,
                        opacity: 0.75,
                    }}
                >
                    Manage your dog profiles and their nutrition plans
                </Typography>
            </Box>

            {!hasDogs ? (
                <QuizStyledCard>
                    <Box
                        sx={{
                            textAlign: "center",
                            py: { xs: 6, md: 8 },
                        }}
                    >
                        <Box
                            sx={{
                                width: { xs: 100, md: 120 },
                                height: { xs: 100, md: 120 },
                                borderRadius: "50%",
                                backgroundColor: QUIZ.color.cream2,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                mx: "auto",
                                mb: 3,
                            }}
                        >
                            <PetsIcon sx={{ fontSize: { xs: 50, md: 60 }, color: QUIZ.color.brown }} />
                        </Box>
                        <Typography
                            sx={{
                                fontSize: { xs: 20, md: 24 },
                                fontWeight: 800,
                                color: QUIZ.color.brown,
                                mb: 1,
                            }}
                        >
                            No dog profiles yet
                        </Typography>
                        <Typography
                            sx={{
                                fontSize: { xs: 14, md: 16 },
                                color: QUIZ.color.brown,
                                opacity: 0.75,
                                mb: 4,
                                maxWidth: 400,
                                mx: "auto",
                            }}
                        >
                            Complete our quiz to create a personalized nutrition plan for your furry friend
                        </Typography>
                        <QuizButton variant="primary" href="/pages/quiz-1">
                            Start the Quiz
                        </QuizButton>
                    </Box>
                </QuizStyledCard>
            ) : (
                <Grid container spacing={{ xs: 2, md: 3 }}>
                    {sortedDogs.map((dog) => (
                        <Grid size={{ xs: 12, md: 6 }} key={String(dog.id)}>
                            <DogSummaryCard
                                dog={dog}
                                onViewProfile={onViewDog}
                                onEdit={onEditDog}
                                onDelete={onDeleteDog}
                                onRestore={onRestoreDog}
                                canDelete={activeDogCount > 1}
                            />
                        </Grid>
                    ))}
                </Grid>
            )}
        </Box>
    );
}
